import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { useMedia } from '@/shared/hooks';

import { TransformedPhoto } from '@/features/transformedPhoto';

import { MAX_PHOTOS_IN_COLLAGE, PHOTO_COLLAGE_ROWS_GAP } from '../../config';

import styles from './PhotoCollage.module.scss';

type PhotoCollageProps = {
  photoIDs: string[];
  className?: string;
  testID?: string;
};

export const PhotoCollage: FC<PhotoCollageProps> = (props) => {
  const { photoIDs, className, testID } = props;
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const displayPhotos = photoIDs.slice(0, MAX_PHOTOS_IN_COLLAGE);

  const photoCount = displayPhotos.length;

  if (photoCount === 0) {
    return null;
  }

  const actualGap = isMobile
    ? PHOTO_COLLAGE_ROWS_GAP / 2
    : PHOTO_COLLAGE_ROWS_GAP;

  const layoutHeights = {
    full: '100%',
    large: `calc(65% - ${actualGap / 2}px)`,
    medium: `calc(60% - ${actualGap / 2}px)`,
    small: `calc(50% - ${actualGap / 2}px)`,
    smaller: `calc(40% - ${actualGap / 2}px)`,
    smallest: `calc(35% - ${actualGap / 2}px)`,
    none: '0%',
  };

  const getLayout = (count: number) => {
    switch (count) {
      case 1:
        return {
          topRow: displayPhotos,
          bottomRow: [],
          topRowHeight: layoutHeights.full,
          bottomRowHeight: layoutHeights.none,
          topRowColumns: 1,
          bottomRowColumns: 1,
        };
      case 2:
        return {
          topRow: displayPhotos,
          bottomRow: [],
          topRowHeight: layoutHeights.full,
          bottomRowHeight: layoutHeights.none,
          topRowColumns: 2,
          bottomRowColumns: 1,
        };
      case 3:
        return {
          topRow: displayPhotos,
          bottomRow: [],
          topRowHeight: layoutHeights.full,
          bottomRowHeight: layoutHeights.none,
          topRowColumns: 3,
          bottomRowColumns: 1,
        };
      case 4:
        return {
          topRow: displayPhotos.slice(0, 2),
          bottomRow: displayPhotos.slice(2),
          topRowHeight: layoutHeights.small,
          bottomRowHeight: layoutHeights.small,
          topRowColumns: 2,
          bottomRowColumns: 2,
        };
      case 5:
        return {
          topRow: displayPhotos.slice(0, 2),
          bottomRow: displayPhotos.slice(2),
          topRowHeight: layoutHeights.medium,
          bottomRowHeight: layoutHeights.smaller,
          topRowColumns: 2,
          bottomRowColumns: 3,
        };
      case 6:
        return {
          topRow: displayPhotos.slice(0, 3),
          bottomRow: displayPhotos.slice(3),
          topRowHeight: layoutHeights.medium,
          bottomRowHeight: layoutHeights.smaller,
          topRowColumns: 3,
          bottomRowColumns: 3,
        };
      case 7:
        return {
          topRow: displayPhotos.slice(0, 3),
          bottomRow: displayPhotos.slice(3),
          topRowHeight: layoutHeights.large,
          bottomRowHeight: layoutHeights.smallest,
          topRowColumns: 3,
          bottomRowColumns: 4,
        };
      case 8:
        return {
          topRow: displayPhotos.slice(0, 3),
          bottomRow: displayPhotos.slice(3),
          topRowHeight: layoutHeights.large,
          bottomRowHeight: layoutHeights.smallest,
          topRowColumns: 3,
          bottomRowColumns: 5,
        };
      case 9:
      default:
        return {
          topRow: displayPhotos.slice(0, 3),
          bottomRow: displayPhotos.slice(3),
          topRowHeight: layoutHeights.large,
          bottomRowHeight: layoutHeights.smallest,
          topRowColumns: 3,
          bottomRowColumns: 6,
        };
    }
  };

  const layout = getLayout(photoCount);

  return (
    <div
      data-testid={testID}
      data-component="photo-collage"
      className={cn(styles.container, className)}
      style={{
        gap: `${actualGap}px`,
      }}
    >
      <div
        className={styles.topRow}
        style={{
          height: layout.topRowHeight,
          gridTemplateColumns: `repeat(${layout.topRowColumns}, 1fr)`,
        }}
      >
        {layout.topRow.map((photoID) => (
          <div key={photoID} className={styles.photo}>
            <TransformedPhoto
              photoID={photoID}
              size="preview"
              alt={formatMessage({
                id: 'imgAltText.dentalPhotoPreview',
                defaultMessage: 'Dental photo preview',
              })}
              className={styles.image}
            />
          </div>
        ))}
      </div>

      {layout.bottomRow.length > 0 && (
        <div
          className={styles.bottomRow}
          style={{
            height: layout.bottomRowHeight,
            gridTemplateColumns: `repeat(${layout.bottomRowColumns}, 1fr)`,
          }}
        >
          {layout.bottomRow.map((photoID) => (
            <div key={photoID} className={styles.photo}>
              <TransformedPhoto
                photoID={photoID}
                size="preview"
                alt={formatMessage({
                  id: 'imgAltText.dentalPhotoPreview',
                  defaultMessage: 'Dental photo preview',
                })}
                className={styles.image}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
